import React, { useEffect, useMemo, useState } from 'react'
import { BlocReferentiel, FieldOption, FiltreSocle, SousBlocOption, TemplateFieldReferentiel } from "../../../types/ChampsObligatoires"
import { REQUIRED_FIELD_STATES, RequiredFieldStateCode } from "../../../enums/EtatChampObligatoire"
import { TypeChamp, TypeChampLabel } from "../../../enums/TypeChamp"
import EtatToggle from './EtatToggle'
import useBlocAdminEditing from '../admin/useBlocAdminEditing'
import BlocAdminPanel from '../admin/BlocAdminPanel'
import { TemplateFieldEditFields, TemplateFieldRowActions, TemplateFieldSocleCheckbox } from '../admin/TemplateFieldAdminControls'

interface SousGroupe {
    key: string
    label: string | null
    rank: number
    fields: TemplateFieldReferentiel[]
}

function groupBySousBloc(fields: TemplateFieldReferentiel[]): SousGroupe[] {
    const groupes = new Map<string, SousGroupe>()

    for (const champ of fields) {
        const key = champ.subBlockId != null ? String(champ.subBlockId) : '__none__'
        let groupe = groupes.get(key)
        if (!groupe) {
            groupe = { key, label: champ.subBlockLabel, rank: champ.subBlockRank ?? -1, fields: [] }
            groupes.set(key, groupe)
        }
        groupe.fields.push(champ)
    }

    const result = Array.from(groupes.values())
    result.forEach(g => g.fields.sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel, 'fr')))
    result.sort((a, b) => a.rank - b.rank)
    return result
}

interface BlocCardProps {
    bloc: BlocReferentiel
    isOpen: boolean
    onToggleOpen: () => void
    getEtat: (templateFieldId: number) => RequiredFieldStateCode | undefined
    onEtatChange: (templateFieldId: number, state: RequiredFieldStateCode) => void
    onBulkEtatChange: (templateFieldIds: number[], state: RequiredFieldStateCode) => void
    filterText: string
    filtreSocle: FiltreSocle
    isTypeChampAvailable: (type: string | null) => boolean
    adminMode: boolean
    fields: FieldOption[]
    onCreateField: (label: string, code: string) => Promise<FieldOption>
    onFetchSubBlocs: (blockId: number) => Promise<SousBlocOption[]>
    onCreateSubBloc: (label: string) => Promise<{ id: number }>
    onCreateTemplateField: (payload: {
        fieldId: number
        subBlockId: number | null
        type: string | null
        isCore: boolean
        defaultState: RequiredFieldStateCode
    }) => Promise<void>
    onUpdateTemplateFieldCore: (templateFieldId: number, isCore: boolean) => Promise<void>
    onUpdateTemplateFieldDetails: (templateFieldId: number, label: string, subBlockId: number | null) => Promise<void>
    onDeleteTemplateField: (templateFieldId: number) => Promise<void>
    reload: () => Promise<void>
}

export default function BlocCard({
    bloc, isOpen, onToggleOpen, getEtat, onEtatChange, onBulkEtatChange, filterText, filtreSocle, isTypeChampAvailable,
    adminMode, fields, onCreateField, onFetchSubBlocs, onCreateSubBloc, onCreateTemplateField, onUpdateTemplateFieldCore, onUpdateTemplateFieldDetails, onDeleteTemplateField, reload,
}: BlocCardProps) {
    // Toute la partie édition du référentiel (BDREF3) est isolée dans ce hook/ces composants :
    // BlocCard ne s'occupe que de l'affichage "réel" (filtrage, regroupement, état par groupe).
    const admin = useBlocAdminEditing({
        blocId: bloc.id,
        adminMode,
        onFetchSubBlocs,
        onCreateTemplateField,
        onUpdateTemplateFieldCore,
        onUpdateTemplateFieldDetails,
        onDeleteTemplateField,
        reload,
    })

    // N'affiche que les champs dont le type (Autobiz/Autovista/.../ManualEntry...) est
    // effectivement disponible pour le groupe (catalogue actif ou activité présente).
    const champsDisponibles = useMemo(
        () => bloc.fields.filter(f => isTypeChampAvailable(f.type)),
        [bloc.fields, isTypeChampAvailable]
    )

    const typesDistincts = useMemo(
        () => Array.from(new Set(champsDisponibles.map(f => f.type).filter((t): t is string => !!t)))
            .sort((a, b) => (TypeChampLabel[a as TypeChamp] ?? a).localeCompare(TypeChampLabel[b as TypeChamp] ?? b, 'fr')),
        [champsDisponibles]
    )
    const [typeSelectionne, setTypeSelectionne] = useState<string | null>(typesDistincts[0] ?? null)

    // Les catalogues/activités du groupe se chargent de façon asynchrone : une fois disponibles,
    // recale la sélection si elle est vide ou n'est plus dans la liste des types affichés.
    useEffect(() => {
        if (typesDistincts.length > 0 && (typeSelectionne === null || !typesDistincts.includes(typeSelectionne))) {
            setTypeSelectionne(typesDistincts[0])
        }
    }, [typesDistincts])

    const champsDuType = typesDistincts.length > 1
        ? champsDisponibles.filter(f => f.type === typeSelectionne)
        : champsDisponibles

    const champsSocleCount = champsDuType.filter(f => f.isCore).length
    const champsParametrablesCount = champsDuType.filter(f => !f.isCore).length

    const correspondAuFiltre = (f: TemplateFieldReferentiel) =>
        (!filterText || f.fieldLabel.toLowerCase().includes(filterText.toLowerCase()))
        && (filtreSocle === 'tous' || (filtreSocle === 'socle' ? f.isCore : !f.isCore))

    const champsAffiches = champsDuType.filter(correspondAuFiltre)

    // Pendant une recherche, un résultat peut se trouver dans un autre sous-onglet "type" que
    // celui actuellement sélectionné : on compte les résultats par type (tous types confondus,
    // pas seulement celui affiché) pour l'indiquer sur les onglets et basculer automatiquement
    // dessus si l'onglet actif n'a lui-même aucun résultat.
    const resultatsParType = useMemo(() => {
        if (!filterText || typesDistincts.length <= 1) return null
        const compteur = new Map<string, number>()
        champsDisponibles.filter(correspondAuFiltre).forEach(f => {
            if (f.type) compteur.set(f.type, (compteur.get(f.type) ?? 0) + 1)
        })
        return compteur
    }, [champsDisponibles, filterText, filtreSocle, typesDistincts])

    // Ne se déclenche que sur un changement de recherche/filtre volontaire (pas sur chaque
    // re-rendu) pour ne pas écraser un clic manuel sur un autre onglet.
    useEffect(() => {
        if (!resultatsParType || resultatsParType.size === 0) return
        if (typeSelectionne && resultatsParType.has(typeSelectionne)) return
        const premierTypeAvecResultat = typesDistincts.find(t => resultatsParType.has(t))
        if (premierTypeAvecResultat) setTypeSelectionne(premierTypeAvecResultat)
    }, [filterText, filtreSocle])

    const hasColonneSousBloc = champsAffiches.some(f => f.subBlockId != null)
    const groupes = groupBySousBloc(champsAffiches)
    const champsNonSocleAffiches = champsAffiches.filter(f => !f.isCore).map(f => f.templateFieldId)

    return (
        <div className="card mb-2">
            <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                onClick={onToggleOpen}
            >
                <div className="d-flex align-items-center">
                    <span className="font-weight-bold mr-2">{bloc.label}</span>
                    {typesDistincts.length <= 1 && (
                        <>
                            {champsSocleCount > 0 && (
                                <span className="badge badge-secondary mr-2">{champsSocleCount} champ{champsSocleCount > 1 ? 's' : ''} socle</span>
                            )}
                            <span className="text-muted"><small>{champsParametrablesCount} champ{champsParametrablesCount > 1 ? 's' : ''} paramétrable{champsParametrablesCount > 1 ? 's' : ''}</small></span>
                        </>
                    )}
                </div>
                <div className="d-flex align-items-center">
                    {isOpen && champsNonSocleAffiches.length > 0 && (
                        <div className="btn-group btn-group-sm mr-3" onClick={e => e.stopPropagation()}>
                            {REQUIRED_FIELD_STATES.map(etat => (
                                <button
                                    key={etat.code}
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => onBulkEtatChange(champsNonSocleAffiches, etat.code)}
                                >
                                    Tout {etat.label.toLowerCase()}
                                </button>
                            ))}
                        </div>
                    )}
                    <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: '#000' }} />
                </div>
            </div>

            {isOpen && (
                <div className="card-body p-3">
                    {adminMode && (
                        <BlocAdminPanel
                            fields={fields}
                            sousBlocsExistants={admin.sousBlocsExistants}
                            showAjoutForm={admin.showAjoutForm}
                            onShowAjoutForm={() => admin.setShowAjoutForm(true)}
                            onCancelAjoutForm={() => admin.setShowAjoutForm(false)}
                            onCreateField={onCreateField}
                            onCreateSubBloc={onCreateSubBloc}
                            onAjoutSubmit={admin.handleAjoutSubmit}
                            editDraftsCount={admin.editDrafts.size}
                            hasEmptyDraftLabel={admin.hasEmptyDraftLabel}
                            savingDetails={admin.savingDetails}
                            onCancelAllEdits={admin.handleCancelAllEdits}
                            onSaveAllEdits={admin.handleSaveAllEdits}
                        />
                    )}

                    {typesDistincts.length > 1 && (
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <ul className="nav nav-tabs mb-0">
                                {typesDistincts.map(type => (
                                    <li className="nav-item" key={type}>
                                        <a
                                            href="#"
                                            className={"nav-link" + (type === typeSelectionne ? " active" : "")}
                                            onClick={e => { e.preventDefault(); setTypeSelectionne(type) }}
                                        >
                                            {TypeChampLabel[type as TypeChamp] ?? type}
                                            {!!resultatsParType?.get(type) && (
                                                <span className="badge badge-pill badge-danger ml-1">{resultatsParType.get(type)}</span>
                                            )}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <div>
                                {champsSocleCount > 0 && (
                                    <span className="badge badge-secondary mr-2">{champsSocleCount} champ{champsSocleCount > 1 ? 's' : ''} socle</span>
                                )}
                                <span className="text-muted"><small>{champsParametrablesCount} champ{champsParametrablesCount > 1 ? 's' : ''} paramétrable{champsParametrablesCount > 1 ? 's' : ''}</small></span>
                            </div>
                        </div>
                    )}

                    {champsAffiches.length === 0 ? (
                        <div className="alert alert-info mb-0">
                            <i className="fa fa-info-circle mr-2" />
                            {filtreSocle === 'parametrable' && champsDuType.length > 0
                                ? "Ce bloc ne contient que des champs socle."
                                : filtreSocle === 'socle' && champsDuType.length > 0
                                    ? "Ce bloc ne contient aucun champ socle."
                                    : (bloc.message || "Rien de paramétrable sur ce bloc.")}
                        </div>
                    ) : (
                        <table className="table table-striped table-sm mb-0">
                            <thead>
                                <tr>
                                    {hasColonneSousBloc && <th style={{ width: '15%' }}>Bloc</th>}
                                    <th style={{ width: '100%' }}>Champ</th>
                                    <th className="text-right" style={{ whiteSpace: 'nowrap' }}>État dans le parcours</th>
                                    {adminMode && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {groupes.map(groupe => groupe.fields.map((champ, index) => {
                                    const draft = admin.editDrafts.get(champ.templateFieldId)
                                    return (
                                    <tr key={champ.templateFieldId}>
                                        {hasColonneSousBloc && (
                                            <td className="align-middle">{index === 0 ? groupe.label : ''}</td>
                                        )}
                                        {draft ? (
                                            <td className="align-middle">
                                                <TemplateFieldEditFields
                                                    label={draft.label}
                                                    sousBloc={draft.sousBloc}
                                                    sousBlocOptions={admin.sousBlocOptions}
                                                    disabled={admin.savingDetails}
                                                    onLabelChange={label => admin.handleUpdateDraft(champ.templateFieldId, { label })}
                                                    onSousBlocChange={sousBloc => admin.handleUpdateDraft(champ.templateFieldId, { sousBloc })}
                                                />
                                            </td>
                                        ) : (
                                            <td className="align-middle">
                                                {champ.fieldLabel}
                                                {adminMode ? (
                                                    <TemplateFieldSocleCheckbox
                                                        checked={champ.isCore}
                                                        disabled={admin.updatingCoreId === champ.templateFieldId}
                                                        onChange={isCore => admin.handleToggleCore(champ.templateFieldId, isCore)}
                                                    />
                                                ) : champ.isCore && (
                                                    <span className="badge badge-light border ml-2">
                                                        <i className="fa fa-lock mr-1" />Champ socle
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td className="text-right align-middle">
                                            <EtatToggle
                                                value={champ.isCore ? 'REQUIRED' : (getEtat(champ.templateFieldId) ?? champ.defaultState)}
                                                onChange={state => onEtatChange(champ.templateFieldId, state)}
                                                disabled={champ.isCore}
                                            />
                                        </td>
                                        {adminMode && (
                                            <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>
                                                <TemplateFieldRowActions
                                                    isEditing={!!draft}
                                                    savingDetails={admin.savingDetails}
                                                    onEdit={() => admin.handleStartEdit(champ)}
                                                    onDelete={() => admin.handleDelete(champ)}
                                                    onCancelEdit={() => admin.handleCancelEdit(champ.templateFieldId)}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                    )
                                }))}
                            </tbody>
                        </table>
                    )}

                    {champsAffiches.length > 0 && champsParametrablesCount === 0 && (
                        <div className="alert alert-info mt-3 mb-0">
                            <i className="fa fa-info-circle mr-2" />
                            {bloc.message || "Rien de paramétrable sur ce bloc."}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
